/*
 * Karin Sanat — <palette-swatches> custom element
 * Arrow-scrollable colour swatch strip on product cards.
 * Markup: snippets/pcard-palette-swatch.liquid
 */
(function () {
  if (customElements.get('palette-swatches')) return;

  /* ---------------------------------------------------------------------------
   * "Second image" swatch source (setting: pcard_palette_image_source).
   * A swatch stamped with data-second-image="{handle}" has no picture in Liquid
   * (a size product is a doubly-nested metaobject reference, so Shopify won't
   * hydrate its images array). We fetch the linked product's 2nd image — the
   * colour chart — and paint it into the swatch's --swatch-image CSS variable.
   *
   * Mirrors the product page (sections/karin-product-variants.liquid): hits the
   * tiny ?view=karin-swatch-img template (prints just the URL, or "none"),
   * falling back to /products/{handle}.js, and SHARES its per-session cache
   * (sessionStorage key `karin:2img:{handle}`) so a colour resolved on the
   * product page is instant here and vice-versa.
   * ------------------------------------------------------------------------- */
  var IMG_CACHE = Object.create(null); // handle -> 2nd-image URL | null | in-flight Promise
  var IMG1_CACHE = Object.create(null); // handle -> featured (1st) image URL | null | in-flight Promise
  var observer = null; // ONE shared IntersectionObserver across every card on the page

  function productPath(handle) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'products/' + encodeURIComponent(handle);
  }

  // LOCALE-SAFE fetch base: prefer the Liquid-emitted product URL (correct locale prefix + locale-translated
  // handle), stripped of query/hash; else rebuild from handle + routes.root. Rebuilding mismatches when a
  // product's handle is translated per locale (…-oil-paint-… vs …-yagli-boya-…), so ?view=/.js 404s or
  // redirects and the swatch never fills on that locale. The resolved CDN URL is locale-independent, so the
  // cache stays keyed by handle.
  function baseFor(handle, url) {
    if (url) return url.split('#')[0].split('?')[0];
    return productPath(handle);
  }

  function readStore(handle, prefix) {
    try {
      var v = sessionStorage.getItem((prefix || 'karin:2img:') + handle);
      if (v === null) return undefined; // not stored
      return v === '' ? null : v; // '' = stored as "no such image"
    } catch (e) {
      return undefined;
    }
  }
  function writeStore(handle, url, prefix) {
    try {
      sessionStorage.setItem((prefix || 'karin:2img:') + handle, url == null ? '' : url);
    } catch (e) {}
  }

  // Shopify CDN images support a `width` param — keep swatch downloads tiny.
  function sized(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function fromProductJs(handle, url) {
    return fetch(baseFor(handle, url) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var images = (data && data.images) || [];
        // Opportunistically cache the featured image too — swapSwatch's last-resort
        // fallback needs it, and this saves it a second .js round-trip.
        var first = images.length ? images[0] : null;
        IMG1_CACHE[handle] = first;
        writeStore(handle, first, 'karin:1img:');
        return images.length > 1 ? images[1] : null;
      });
  }

  // Featured (1st) image of a product — the last-resort swatch fill when the product has no
  // 2nd image AND the Liquid-stamped fallback is missing or broken. Same cache discipline as
  // fetchSecondImage (in-memory + sessionStorage + shared in-flight request).
  function fetchFirstImage(handle, url) {
    var cached = IMG1_CACHE[handle];
    if (cached !== undefined) {
      return cached && typeof cached.then === 'function' ? cached : Promise.resolve(cached);
    }
    var stored = readStore(handle, 'karin:1img:');
    if (stored !== undefined) {
      IMG1_CACHE[handle] = stored;
      return Promise.resolve(stored);
    }
    var request = fetch(baseFor(handle, url) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var images = (data && data.images) || [];
        var url = images.length ? images[0] : null;
        IMG1_CACHE[handle] = url;
        writeStore(handle, url, 'karin:1img:');
        return url;
      })
      .catch(function () {
        IMG1_CACHE[handle] = null;
        return null;
      });
    IMG1_CACHE[handle] = request;
    return request;
  }

  function resolveUrl(handle, url) {
    return fetch(baseFor(handle, url) + '?view=karin-swatch-img', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (body) {
        var text = (body || '').trim();
        if (text === 'none') return null; // view ran; this product has no 2nd image
        if (text && text.indexOf('<') === -1 && text.length < 2000 && /(^https?:|^\/\/)/.test(text)) {
          return text;
        }
        return fromProductJs(handle, url); // view missing/invalid → full-product fallback
      })
      .catch(function () { return fromProductJs(handle, url); });
  }

  function fetchSecondImage(handle, url) {
    var cached = IMG_CACHE[handle];
    if (cached !== undefined) {
      return cached && typeof cached.then === 'function' ? cached : Promise.resolve(cached);
    }
    var stored = readStore(handle);
    if (stored !== undefined) {
      IMG_CACHE[handle] = stored;
      return Promise.resolve(stored);
    }
    var request = resolveUrl(handle, url)
      .then(function (url) {
        IMG_CACHE[handle] = url;
        writeStore(handle, url);
        return url;
      })
      .catch(function () {
        IMG_CACHE[handle] = null;
        return null;
      });
    IMG_CACHE[handle] = request; // duplicate handles share the in-flight request
    return request;
  }

  function paint(swatch, url) {
    swatch.style.setProperty('--swatch-image', 'url("' + url + '")');
  }

  // Paint only if the URL actually loads. The Liquid-stamped fallback comes from a doubly-nested
  // metaobject reference, where `featured_image`/`swatch_image` can hydrate truthy-but-BROKEN —
  // painting that leaves an empty circle even though an attr was present.
  function paintIfLoadable(swatch, url) {
    return new Promise(function (resolve) {
      if (!url) return resolve(false);
      var img = new Image();
      img.onload = function () { paint(swatch, url); resolve(true); };
      img.onerror = function () { resolve(false); };
      img.src = url;
    });
  }

  function swapSwatch(swatch) {
    if (!swatch || swatch._secondDone) return;
    swatch._secondDone = true;
    var handle = swatch.getAttribute('data-second-image');
    if (!handle) return;
    var purl = swatch.getAttribute('data-second-image-url') || '';
    fetchSecondImage(handle, purl).then(function (url) {
      if (url) return paint(swatch, url); // the colour chart — the real thing
      // No 2nd image on the product. Fallback chain, never an empty circle:
      // 1) the Liquid-stamped hint (colour's Swatch Image, else featured) — validated, and
      // 2) the product's REAL featured image fetched via .js when the hint is missing/broken.
      var fb = swatch.getAttribute('data-second-fallback');
      paintIfLoadable(swatch, fb).then(function (ok) {
        if (ok) return;
        fetchFirstImage(handle, purl).then(function (first) {
          if (first) paint(swatch, sized(first, 192));
        });
      });
    });
  }

  function getObserver() {
    if (observer) return observer;
    if (!('IntersectionObserver' in window)) return null;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          swapSwatch(entry.target);
        });
      },
      { rootMargin: '200px' } // fetch just before a swatch scrolls into view
    );
    return observer;
  }

  customElements.define('palette-swatches', class PaletteSwatches extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-palette-track]');
      this.navGroup = this.querySelector('[data-palette-nav]');
      this.prev = this.querySelector('[data-palette-prev]');
      this.next = this.querySelector('[data-palette-next]');
      this.current = this.querySelector('.pcard-palette__swatch.is-current');

      // Fill any "second image" swatches on this card once they near the viewport.
      this._secondSwatches = Array.prototype.slice.call(
        this.querySelectorAll('.pcard-palette__swatch[data-second-image]')
      );
      if (this._secondSwatches.length) {
        var obs = getObserver();
        if (obs) this._secondSwatches.forEach(function (s) { obs.observe(s); });
        else this._secondSwatches.forEach(swapSwatch); // no IO support: fetch immediately
      }

      if (!this.track) return;

      this._onScroll = this.update.bind(this);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

      // React to layout/width changes (responsive columns, lazy images loading).
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.update());
        this._ro.observe(this.track);
      }
      // Three chances to center the active swatch, so no card is ever missed:
      //  1) next frame, 2) any layout change (ResizeObserver above),
      //  3) once the whole page + fonts have settled.
      requestAnimationFrame(() => this.update());
      window.addEventListener('load', () => this.update(), { once: true });
    }

    disconnectedCallback() {
      if (this.track && this._onScroll) this.track.removeEventListener('scroll', this._onScroll);
      if (this._ro) this._ro.disconnect();
      // AJAX filter/sort (facets.js) replaces the grid HTML; drop this card's swatches from the
      // shared observer so detached nodes aren't left observed.
      if (this._secondSwatches && observer) {
        this._secondSwatches.forEach(function (s) { observer.unobserve(s); });
      }
    }

    get overflowing() {
      return this.track.scrollWidth - this.track.clientWidth > 1;
    }

    scrollByPage(dir) {
      // Scroll by ~90% of the visible width so a swatch isn't left half-cut.
      this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: 'smooth' });
    }

    // Bring THIS card's own colour swatch into view so the active ring is
    // visible on every card; the < > arrows then handle browsing the rest.
    centerCurrent() {
      if (!this.current) return;
      const item = this.current.closest('.pcard-palette__item') || this.current;
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const delta = (itemRect.left - trackRect.left) - (this.track.clientWidth - itemRect.width) / 2;
      this.track.scrollLeft += delta;
    }

    update() {
      if (!this.track || !this.isConnected) return;
      const overflowing = this.overflowing;
      if (this.navGroup) this.navGroup.hidden = !overflowing;
      if (!overflowing) return;

      // Center the active swatch once it (and a scrollable track) exist.
      // Instant jump — CSS no longer sets scroll-behavior: smooth, so this
      // can't be interrupted by page-load scroll activity like before.
      if (!this._centered && this.current) {
        this._centered = true;
        this.centerCurrent();
      }

      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      // scrollLeft can be negative in RTL; compare on magnitude.
      const atStart = Math.abs(x) <= 1;
      const atEnd = Math.abs(Math.abs(x) - max) <= 1;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }
  });
})();
